/** Left-to-Right Mark — keeps URLs/numbers in logical order after RTL text like «لغو». */
const LRM = '\u200E';

const LTR_MARK_PATTERN = /[\u200e\u200f]/g;
const DIGITS_ONLY_PATTERN = /^[0-9۰-۹٠-٩]+$/;

/** Link-like keywords (domain/path); pure numbers are not links. */
export function isCancellationLink(keyword: string): boolean {
  const value = keyword.trim();
  if (!value || DIGITS_ONLY_PATTERN.test(value)) {
    return false;
  }
  return /[./]/.test(value) || /^https?:\/\//i.test(value);
}

/** Prefix keyword with LRM so leading digits stay in place (e.g. `11.sms.ir/rs`). */
export function withLtrMark(value: string): string {
  if (!value) {
    return '';
  }
  return value.startsWith(LRM) ? value : `${LRM}${value}`;
}

/** Remove display-only LTR/RTL marks before SMS length calculation. */
export function stripLtrMarks(value: string): string {
  return value.replace(LTR_MARK_PATTERN, '');
}

/**
 * Builds «لغو»/«OFF» + keyword.
 * Adds a space only when the keyword looks like a link.
 */
export function buildCancellationLabel(
  keyword: string,
  hasDoubleChar: boolean,
  options?: {forDisplay?: boolean},
): string {
  if (!keyword) {
    return '';
  }

  const prefix = hasDoubleChar ? 'لغو' : 'OFF';
  const separator = isCancellationLink(keyword) ? ' ' : '';
  const body = options?.forDisplay ? withLtrMark(keyword) : keyword;
  return `${prefix}${separator}${body}`;
}

/**
 * Formats an existing cancellation label for UI:
 * ensures link spacing + LRM on the keyword part.
 */
export function formatCancellationLabelForDisplay(label: string): string {
  if (!label) {
    return '';
  }

  let prefix = '';
  let rest = label;

  if (label.startsWith('لغو')) {
    prefix = 'لغو';
    rest = label.slice('لغو'.length);
  } else if (label.startsWith('OFF')) {
    prefix = 'OFF';
    rest = label.slice(3);
  } else {
    return withLtrMark(label);
  }

  rest = rest.replace(/^\s+/, '');
  const separator = isCancellationLink(rest) ? ' ' : '';
  return `${prefix}${separator}${withLtrMark(rest)}`;
}
